import axios from 'axios'

import type { ExtendedChain } from '@lifi/sdk'
import { useQuery } from '@tanstack/react-query'

import { allChains } from '@src/configs/chainConfig'
import {
    ChainId,
    ChainName,
    getChainNameFromChainId,
    getChainType,
    getGasTokenSymbol,
    isTestnet,
} from '@src/configs/chainInfo'
import { LIFI_CHAIN_FALLBACK } from '@src/constants/exchange'
import { createContextLogger } from '@src/utils/logger'

const queryKey = ['chainStats']
const logger = createContextLogger('useChains')

export interface ChainProps {
    chains: ExtendedChain[]
    isSuccess: boolean
    isError: boolean
    error: Error | null
}

export async function getChainsQuery() {
    try {
        // Use axios instead of fetch
        const { data } = await axios.get('https://worker1.mach.exchange/chains', {
            headers: {
                'Content-Type': 'application/json',
            },
            timeout: 10000, // 10-second timeout
        })

        /**
         * WORKAROUND: Check missing chains and create fallbacks
         */

        // Validate that all chains have configurations from the worker
        // and get any missing chains
        const missingChains = validateChainsConfiguration(data.chains || [])

        // If there are missing chains, create fallbacks for them and merge with API results
        if (missingChains.length > 0) {
            logger.info('Adding fallback configurations for missing chains', {
                missingChainCount: missingChains.length,
                missingChainNames: missingChains.map(chain => chain.name),
            })

            // Create fallback data for missing chains
            const fallbackChains = missingChains.map(createFallbackChainData)

            // Merge API chains with fallback chains
            data.chains = [...(data.chains || []), ...fallbackChains]
        }

        return data
    } catch (error) {
        logger.error('Failed to fetch chain data from API', { error })

        // WORKAROUND: Construct full fallback from allChains
        // This is a temporary solution until worker1 API is fixed to include all chains
        logger.info('Constructing complete fallback data from allChains')
        const fallbackChains = allChains.map(createFallbackChainData)

        return { chains: fallbackChains }
    }
}

/**
 * Validate that all chains have corresponding configurations from the worker
 * @param workerChains - Chain configurations returned from the worker API
 * @returns Array of chains that don't have worker configurations
 */
function validateChainsConfiguration(workerChains: ExtendedChain[]) {
    // Create a map of chain IDs from worker response for easy lookup
    const workerChainMap = new Map(workerChains.map(chain => [chain.id, chain]))

    // Check if each supported chain has a configuration from the worker
    const missingChains = allChains.filter(chain => !workerChainMap.has(chain.id))

    if (missingChains.length > 0) {
        logger.error('Missing chain configurations from worker1 API', {
            missingChains: missingChains.map(chain => ({
                id: chain.id,
                name: chain.name,
            })),
            supportedChainIds: allChains.map(chain => chain.id),
            workerChainIds: Array.from(workerChainMap.keys()),
        })
    }

    return missingChains
}

/**
 * Creates a fallback ExtendedChain object based on a supported chain
 *
 * WORKAROUND: This is a temporary solution to handle missing chain data from worker1 API.
 * The worker1 API should be updated to provide data for all chains.
 *
 * @param chain - A chain from allChains
 * @returns An ExtendedChain object compatible with the application
 */
function createFallbackChainData(chain: (typeof allChains)[0]): ExtendedChain {
    // Extract chain ID
    const { id } = chain

    // Use utility functions from chainInfo.ts to get chain details
    const name = chain.name
    const chainType = getChainType(id as ChainId)
    const nativeTokenSymbol = chain.nativeCurrency.symbol
    const testnet = isTestnet(id as ChainId)

    // Construct the basic fallback object that matches worker1 API response format
    return {
        id,
        name,
        key: name.toLowerCase(),
        chainType,
        coin: nativeTokenSymbol,
        mainnet: !testnet,
        testnet,
        logoURI: `/assets/network/${getChainNameFromChainId(id as ChainId)}.png`,
        multicallAddress: '', // This would need to be filled with actual multicall addresses
        metamask: {
            chainId: `0x${id.toString(16)}`,
            blockExplorerUrls: [chain.blockExplorers?.default?.url || ''],
            chainName: name,
            nativeCurrency: {
                name: chain.nativeCurrency.name,
                symbol: nativeTokenSymbol,
                decimals: chain.nativeCurrency.decimals,
            },
            rpcUrls: [chain.rpcUrls.default.http[0] || ''],
        },
        nativeToken: {
            address: '0x0000000000000000000000000000000000000000',
            chainId: id,
            symbol: nativeTokenSymbol,
            decimals: chain.nativeCurrency.decimals,
            name: chain.nativeCurrency.name,
            coinKey: name.toLowerCase(),
            logoURI: `/assets/token/${getGasTokenSymbol(name as ChainName).toLowerCase()}.png`,
            priceUSD: '',
        },
        diamondAddress: '',
        permit2: '',
        permit2Proxy: '',
    } as ExtendedChain
}

export const useChains = (): ChainProps => {
    const { data, isSuccess, isError, error } = useQuery({
        queryKey,
        queryFn: getChainsQuery,
        enabled: true,
        refetchInterval: 1000 * 60 * 60, // Refetch every hour
        retry: 2,
        staleTime: 1000 * 60 * 30, // Consider data stale after 30 minutes
        // Use placeholderData to provide fallback when loading
        placeholderData: { chains: LIFI_CHAIN_FALLBACK },
    })

    return {
        chains: data?.chains || (LIFI_CHAIN_FALLBACK as ExtendedChain[]),
        isSuccess,
        isError,
        error,
    }
}
